import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n";
import {
  CreditCard, Briefcase, ShieldCheck, Coins, PiggyBank, Landmark,
  ArrowRight, ArrowLeft, CheckCircle2,
} from "lucide-react";

export const SERVICES = {
  "online-bank": {
    icon: CreditCard, color: "#635BFF",
    en: {
      title: "Online Crypto Banking",
      lead: "A dedicated crypto-native account that lets you spend your fiat money with digital assets — the way a Coinbase-style card works, supervised by the UCP desk.",
      sections: [
        { h: "A purely crypto account", p: "Open a numbered account denominated in digital assets. Deposit, hold and manage BTC, ETH and stablecoins alongside a USD cash position — all inside your confidential client space." },
        { h: "Spend fiat with crypto", p: "Link your holdings to everyday spending: your crypto balance is converted at live Binance rates at the moment of payment, like leading crypto cards. No manual selling, no timing stress." },
        { h: "Desk-supervised movements", p: "Every deposit and withdrawal is initiated from your wallet tab and validated by our desk before execution — combining banking-grade controls with crypto speed." },
      ],
      bullets: ["Crypto-denominated numbered account", "Live-rate conversion at payment time", "SEPA & multi-network funding rails", "Full movement audit trail"],
    },
    fr: {
      title: "Banque en ligne crypto",
      lead: "Un compte dédié crypto-natif qui vous permet de dépenser votre argent fiduciaire avec vos actifs numériques — à la manière d'une carte type Coinbase, supervisé par le desk UCP.",
      sections: [
        { h: "Un compte purement crypto", p: "Ouvrez un compte numéroté libellé en actifs numériques. Déposez, conservez et gérez BTC, ETH et stablecoins aux côtés d'une position cash en USD — dans votre espace client confidentiel." },
        { h: "Dépensez du fiat avec vos cryptos", p: "Associez vos avoirs à vos dépenses quotidiennes : votre solde crypto est converti au cours Binance en direct au moment du paiement, comme sur les meilleures cartes crypto. Aucune vente manuelle, aucun stress de timing." },
        { h: "Mouvements supervisés par le desk", p: "Chaque dépôt et retrait est initié depuis votre onglet portefeuille puis validé par notre desk avant exécution — alliant les contrôles de niveau bancaire à la vitesse de la crypto." },
      ],
      bullets: ["Compte numéroté libellé en crypto", "Conversion au cours réel au moment du paiement", "Rails de financement SEPA & multi-réseaux", "Piste d'audit complète des mouvements"],
    },
  },
  "asset-management": {
    icon: Briefcase, color: "#0EA5E9",
    en: {
      title: "Digital Asset Management",
      lead: "Institutional portfolio management applied to digital assets: discretionary and advisory mandates, risk budgeting and disciplined rebalancing.",
      sections: [
        { h: "Mandates built around you", p: "Discretionary mandates delegate day-to-day decisions to the desk; advisory mandates keep you in control with our trade ideas and execution support. Both follow a written investment policy statement." },
        { h: "Risk budgeting, not bets", p: "Allocations are sized by volatility contribution, not conviction. Core positions in BTC and ETH, satellite exposure to high-conviction themes, stablecoin buffer for dry powder." },
        { h: "Disciplined rebalancing", p: "Quarterly or threshold-based rebalancing locks in gains and restores target weights — the mechanical discipline that long-term performance is built on." },
      ],
      bullets: ["Discretionary & advisory mandates", "Volatility-based position sizing", "Core-satellite construction", "Quarterly reporting & review calls"],
    },
    fr: {
      title: "Gestion d'actifs numériques",
      lead: "La gestion de portefeuille institutionnelle appliquée aux actifs numériques : mandats discrétionnaires et consultatifs, budgétisation du risque et rééquilibrage discipliné.",
      sections: [
        { h: "Des mandats construits autour de vous", p: "Les mandats discrétionnaires délèguent les décisions quotidiennes au desk ; les mandats consultatifs vous laissent le contrôle avec nos idées de trades et notre support d'exécution. Les deux suivent une politique d'investissement écrite." },
        { h: "Budgétisation du risque, pas des paris", p: "Les allocations sont dimensionnées par contribution à la volatilité, pas par conviction. Positions cœur en BTC et ETH, exposition satellite à des thèmes à forte conviction, réserve en stablecoins." },
        { h: "Rééquilibrage discipliné", p: "Un rééquilibrage trimestriel ou par seuils sécurise les gains et restaure les pondérations cibles — la discipline mécanique sur laquelle se construit la performance de long terme." },
      ],
      bullets: ["Mandats discrétionnaires & consultatifs", "Dimensionnement par la volatilité", "Construction cœur-satellite", "Reporting trimestriel & appels de revue"],
    },
  },
  insurance: {
    icon: ShieldCheck, color: "#059669",
    en: {
      title: "Crypto Asset Insurance",
      lead: "Protect your digital wealth with coverage frameworks inspired by institutional custody standards.",
      sections: [
        { h: "Coverage philosophy", p: "Digital assets held within the UCP environment benefit from layered protection: segregated accounting, cold-storage majority allocation and desk-validated movements on every operation." },
        { h: "Incident response", p: "A dedicated response protocol covers unauthorized access attempts, with connection audits (timestamp, IP, device) available for every session of your account." },
        { h: "Estate & transmission", p: "Prepare the transmission of your digital estate: designated beneficiaries, documented procedures and desk assistance for your heirs." },
      ],
      bullets: ["Segregated client accounting", "98% cold storage coverage", "Connection audit trail per session", "Estate transmission procedures"],
    },
    fr: {
      title: "Assurance des actifs crypto",
      lead: "Protégez votre patrimoine numérique avec des cadres de couverture inspirés des standards institutionnels de conservation.",
      sections: [
        { h: "Philosophie de couverture", p: "Les actifs numériques détenus dans l'environnement UCP bénéficient d'une protection en couches : comptabilité ségrégée, allocation majoritaire en cold storage et mouvements validés par le desk à chaque opération." },
        { h: "Réponse aux incidents", p: "Un protocole de réponse dédié couvre les tentatives d'accès non autorisées, avec un audit des connexions (horodatage, IP, appareil) disponible pour chaque session de votre compte." },
        { h: "Succession & transmission", p: "Préparez la transmission de votre patrimoine numérique : bénéficiaires désignés, procédures documentées et assistance du desk pour vos héritiers." },
      ],
      bullets: ["Comptabilité client ségrégée", "98 % de couverture cold storage", "Piste d'audit des connexions", "Procédures de transmission successorale"],
    },
  },
  "crypto-loans": {
    icon: Coins, color: "#F59E0B",
    en: {
      title: "Crypto-Backed Loans",
      lead: "Unlock liquidity without selling your positions: borrow against your BTC, ETH or stablecoins while keeping your upside.",
      sections: [
        { h: "Liquidity without selling", p: "Pledge your digital assets as collateral and receive a fiat or stablecoin credit line. Your crypto stays invested — you keep full exposure to potential appreciation." },
        { h: "Transparent loan-to-value", p: "Typical LTV starts at 50% with margin alerts at 65% and review at 75%. Rates are fixed at origination and supervision is handled by the desk." },
        { h: "Flexible repayment", p: "Repay at any time without penalty. Collateral is released immediately once the line is closed and validated by the desk." },
      ],
      bullets: ["LTV from 50% with margin alerts", "No sale — keep your upside", "Fixed rates at origination", "Early repayment without penalty"],
    },
    fr: {
      title: "Prêts adossés aux cryptos",
      lead: "Débloquez de la liquidité sans vendre vos positions : empruntez contre vos BTC, ETH ou stablecoins tout en conservant votre potentiel de hausse.",
      sections: [
        { h: "De la liquidité sans vendre", p: "Nantissez vos actifs numériques en garantie et recevez une ligne de crédit en fiat ou en stablecoins. Vos cryptos restent investies — vous conservez toute l'exposition à leur appréciation potentielle." },
        { h: "Loan-to-value transparent", p: "Le LTV démarre typiquement à 50 % avec des alertes de marge à 65 % et une revue à 75 %. Les taux sont fixés à l'origination et la supervision est assurée par le desk." },
        { h: "Remboursement flexible", p: "Remboursez à tout moment sans pénalité. Le collatéral est libéré immédiatement une fois la ligne clôturée et validée par le desk." },
      ],
      bullets: ["LTV dès 50 % avec alertes de marge", "Pas de vente — conservez votre upside", "Taux fixes à l'origination", "Remboursement anticipé sans pénalité"],
    },
  },
  staking: {
    icon: PiggyBank, color: "#EC4899",
    en: {
      title: "Staking & Yield",
      lead: "Put your assets to work: staking programs with transparent yield, flexible terms and desk supervision.",
      sections: [
        { h: "How staking yield works", p: "Proof-of-stake networks reward validators for securing the chain. UCP aggregates institutional-grade staking so you receive network rewards without running any infrastructure." },
        { h: "Terms & liquidity", p: "Choose flexible terms (withdraw anytime) or fixed terms (30/90 days) for enhanced rates. Rewards accrue daily and compound automatically in your portfolio." },
        { h: "Risk framework", p: "Validator selection, slashing protection and network diversification are managed by the desk. Yields are indicative and vary with network conditions." },
      ],
      bullets: ["Flexible or fixed 30/90-day terms", "Daily rewards, auto-compounded", "Slashing-protected validator set", "Transparent indicative APY"],
    },
    fr: {
      title: "Staking & Rendement",
      lead: "Faites travailler vos actifs : des programmes de staking au rendement transparent, à conditions flexibles, supervisés par le desk.",
      sections: [
        { h: "Comment fonctionne le rendement du staking", p: "Les réseaux proof-of-stake récompensent les validateurs qui sécurisent la chaîne. UCP agrège du staking de qualité institutionnelle pour que vous receviez les récompenses du réseau sans gérer aucune infrastructure." },
        { h: "Conditions & liquidité", p: "Choisissez des conditions flexibles (retrait à tout moment) ou fixes (30/90 jours) pour des taux bonifiés. Les récompenses sont comptabilisées quotidiennement et composées automatiquement dans votre portefeuille." },
        { h: "Cadre de risque", p: "La sélection des validateurs, la protection anti-slashing et la diversification des réseaux sont gérées par le desk. Les rendements sont indicatifs et varient selon les conditions du réseau." },
      ],
      bullets: ["Conditions flexibles ou fixes 30/90 jours", "Récompenses quotidiennes auto-composées", "Validateurs protégés anti-slashing", "APY indicatif transparent"],
    },
  },
  "private-bank": {
    icon: Landmark, color: "#7C3AED",
    en: {
      title: "Private Banking",
      lead: "The full UCP private banking relationship: a dedicated advisor, consolidated wealth view and confidential execution.",
      sections: [
        { h: "A dedicated advisor", p: "One point of contact who knows your file: allocation reviews, funding operations, tax space coordination and direct chat access from your client space." },
        { h: "Consolidated wealth view", p: "Your digital assets, cash positions and funding methods consolidated in one confidential dashboard, with exportable history for your records." },
        { h: "Confidential by design", p: "Access is granted only after desk validation. Connection auditing, encrypted sessions and a strict no-data-sale policy protect your privacy." },
      ],
      bullets: ["Dedicated advisor & direct chat", "Consolidated wealth dashboard", "KYC & document vault included", "Strict confidentiality policy"],
    },
    fr: {
      title: "Banque privée",
      lead: "La relation de banque privée UCP complète : un conseiller dédié, une vue patrimoniale consolidée et une exécution confidentielle.",
      sections: [
        { h: "Un conseiller dédié", p: "Un interlocuteur unique qui connaît votre dossier : revues d'allocation, opérations de financement, coordination de l'espace fiscal et accès au chat direct depuis votre espace client." },
        { h: "Vue patrimoniale consolidée", p: "Vos actifs numériques, positions cash et moyens de financement consolidés dans un tableau de bord confidentiel, avec historique exportable pour vos archives." },
        { h: "Confidentiel par conception", p: "L'accès n'est accordé qu'après validation du desk. Audit des connexions, sessions chiffrées et politique stricte de non-cession des données protègent votre vie privée." },
      ],
      bullets: ["Conseiller dédié & chat direct", "Tableau de bord patrimonial consolidé", "KYC & coffre documentaire inclus", "Politique de confidentialité stricte"],
    },
  },
};

export default function Services() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const svc = SERVICES[slug];
  const content = svc?.[lang] || svc?.en;

  if (!svc) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-[#425466]">Service not found. <Link to="/" className="text-[#635BFF] hover:underline">← UCP</Link></p>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = svc.icon;
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="service-page">
      <Navbar />
      <section className="relative overflow-hidden pt-32 pb-20" style={{ background: `linear-gradient(135deg, ${svc.color}14 0%, #ffffff 60%)` }}>
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium hover:underline mb-8" style={{ color: svc.color }}>
            <ArrowLeft size={15} /> UCP
          </Link>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
               style={{ background: `${svc.color}14`, border: `1px solid ${svc.color}33` }}>
            <Icon size={26} style={{ color: svc.color }} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A2540]" data-testid="service-title">
            {content.title}
          </h1>
          <p className="text-[#425466] text-lg mt-5 leading-relaxed max-w-2xl">{content.lead}</p>
        </div>
      </section>
      <main className="flex-1 max-w-4xl mx-auto px-5 lg:px-8 pb-20 w-full">
        <div className="space-y-8">
          {content.sections.map((s) => (
            <section key={s.h} className="bg-[#F6F9FC] border border-[#E6EBF1] rounded-2xl p-7">
              <h2 className="font-display text-lg font-semibold" style={{ color: svc.color }}>{s.h}</h2>
              <p className="text-sm text-[#425466] leading-relaxed mt-2">{s.p}</p>
            </section>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-10">
          {content.bullets.map((b) => (
            <div key={b} className="flex items-start gap-2.5 text-sm text-[#0A2540] font-medium">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: svc.color }} /> {b}
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link to="/register">
            <Button className="bg-[#635BFF] hover:bg-[#0A2540] text-white px-8 h-12 rounded-full font-semibold shadow-xl shadow-[#635BFF]/25" data-testid="service-cta">
              {t("cta.button")} <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
